/* tslint:disable:max-file-line-count */
import * as React from 'react';
import { inject, observer } from 'mobx-react';

import { AnalyticsStore, ContextStore } from 'bernie-core';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { FindAnswersFormLaunchLocation, FindAnswersStore } from 'stores/find-answers-store';

import { PropertyReviewType, Reviews } from '__generated__/api/types';
import _SortAndFilter = Reviews._SortAndFilter;

import { ReviewsFilterOptionsModel } from 'models/common';

import { ReviewsFilterType, TravelerTypeOptions } from 'constants/reviews-filters-type';

import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';

import { DialogContent, DialogContentSplitView } from 'components/infosite/property-details/reviews-dialog-content';

export interface ReviewsDialogProps extends LocalizedComponentProps {
  currentHotel?: CurrentHotelStore;
  context?: ContextStore;
  analytics?: AnalyticsStore;
  min?: number;
  onDismiss?: () => void;
  value?: number;
  onStickyBookButtonClick?: () => void;
  findAnswersStore?: FindAnswersStore;
  splitView?: boolean;
  fullScreen?: boolean;
}

export interface ReviewsDialogState {
  showDialog: boolean;
  showRatingFilterSheet: boolean;
  showTravelerFilterSheet: boolean;
  showLanguageFilterSheet: boolean;
}

@withLocalization
@inject('currentHotel', 'context', 'analytics', 'findAnswersStore')
@observer
export class ReviewsDialog extends React.Component<ReviewsDialogProps, ReviewsDialogState> {
  public constructor(props: ReviewsDialogProps) {
    super(props);

    this.state = {
      showDialog: false,
      showRatingFilterSheet: false,
      showTravelerFilterSheet: false,
      showLanguageFilterSheet: false
    };

    this.onDismiss = this.onDismiss.bind(this);
    this.onMoreReviews = this.onMoreReviews.bind(this);
    this.applyRatingsFilters = this.applyRatingsFilters.bind(this);
    this.applyTravelerTypeFilters = this.applyTravelerTypeFilters.bind(this);
    this.toggleTravelerFilterSheet = this.toggleTravelerFilterSheet.bind(this);
    this.toggleRatingFilterSheet = this.toggleRatingFilterSheet.bind(this);
  }

  public componentDidMount() {
    this.setState({ showDialog: true });
    const { findAnswersStore } = this.props;
    if (findAnswersStore) {
      findAnswersStore.setFormLaunchLocation(FindAnswersFormLaunchLocation.REVIEWS_DIALOG);
    }
  }

  public componentWillUnmount() {
    const { findAnswersStore } = this.props;
    if (findAnswersStore) {
      findAnswersStore.setFormLaunchLocation(FindAnswersFormLaunchLocation.INFOSITE);
    }
  }

  public applyRatingsFilters(filters, shouldFetch?) {
    const { context, currentHotel } = this.props;
    const scoresInNumberType = filters.map(score => Number(score));

    currentHotel.setReviewsScoreFilter(scoresInNumberType);

    if (shouldFetch) {
      currentHotel.fetchFilteredReviews(context, currentHotel.hotelId, true);
    }
  }

  public applyTravelerTypeFilters(filters, shouldFetch?) {
    const { context, currentHotel } = this.props;
    const travelCompanionFilters = [];
    const tripReasonFilters = [];

    filters.forEach(filter => {
      switch (filter) {
        case TravelerTypeOptions.FAMILIES:
          travelCompanionFilters.push(ReviewsFilterType.FAMILY);
          travelCompanionFilters.push(ReviewsFilterType.FAMILY_WITH_CHILDREN);
          break;
        case TravelerTypeOptions.FAMILIES_WITH_CHILDREN:
          if (!travelCompanionFilters.includes(ReviewsFilterType.FAMILY_WITH_CHILDREN)) {
            travelCompanionFilters.push(ReviewsFilterType.FAMILY_WITH_CHILDREN);
          }
          break;
        case TravelerTypeOptions.BUSINESS_TRAVELERS:
          tripReasonFilters.push(ReviewsFilterType.BUSINESS);
          tripReasonFilters.push(ReviewsFilterType.BUSINESS_LEISURE);
          break;
        case TravelerTypeOptions.COUPLES:
          travelCompanionFilters.push(ReviewsFilterType.PARTNER);
          break;
        case TravelerTypeOptions.SOLO_TRAVELERS:
          travelCompanionFilters.push(ReviewsFilterType.SELF);
          break;
        case TravelerTypeOptions.GROUPS:
          travelCompanionFilters.push(ReviewsFilterType.FRIENDS);
          break;
        case TravelerTypeOptions.TRAVELERS_WITH_PETS:
          travelCompanionFilters.push(ReviewsFilterType.PET);
          break;
        default:
          break;
      }
    });

    currentHotel.setTravelerTypeFilters(travelCompanionFilters, tripReasonFilters);

    if (shouldFetch) {
      currentHotel.fetchFilteredReviews(context, currentHotel.hotelId, true);
    }
  }

  public applyLanguageTypeFilters = (filter: string, shouldFetch?: boolean): void => {
    const { context, currentHotel } = this.props;

    currentHotel.setLanguageTypeFilters(filter);

    if (shouldFetch) {
      currentHotel.fetchFilteredReviews(context, currentHotel.hotelId, true);
    }
  }

  private getRatingCounts(ratingValue, allRatingCounts) {
    const matchedRating = allRatingCounts && allRatingCounts.find(ratingData => ratingData.rating === ratingValue);

    return (matchedRating && matchedRating.count && matchedRating.count.formatted) || '0';
  }

  public toggleRatingFilterSheet() {
    this.setState(prevState => ({
      showRatingFilterSheet: !prevState.showRatingFilterSheet,
      showTravelerFilterSheet: false,
      showLanguageFilterSheet: false
    }));
  }

  public toggleTravelerFilterSheet() {
    this.setState(prevState => ({
      showTravelerFilterSheet: !prevState.showTravelerFilterSheet,
      showRatingFilterSheet: false,
      showLanguageFilterSheet: false
    }));
  }

  public toggleLanguageFilterSheet = () => {
    this.setState(prevState => ({
      showLanguageFilterSheet: !prevState.showLanguageFilterSheet,
      showRatingFilterSheet: false,
      showTravelerFilterSheet: false
    }));
  }

  public onMoreReviews(includeRatingsOnlyReview?: boolean, reviewType?: PropertyReviewType) {
    const { context, currentHotel } = this.props;
    const filtersApplied = currentHotel && currentHotel.checkReviewsFiltersApplied;

    if (filtersApplied) {
      currentHotel.fetchFilteredReviews(context, currentHotel.hotelId, includeRatingsOnlyReview);
    } else {
      currentHotel.fetchReviews(context, currentHotel.hotelId, includeRatingsOnlyReview, reviewType);
    }
  }

  public onDismiss() {
    const { findAnswersStore, onDismiss } = this.props;
    if (onDismiss) {
      onDismiss();
    }
    if (findAnswersStore) {
      findAnswersStore.setFormLaunchLocation(FindAnswersFormLaunchLocation.INFOSITE);
    }
  }

  public getLanguageFilterOptions = (sortAndFilter: _SortAndFilter[]): ReviewsFilterOptionsModel[] => {
    if (sortAndFilter) {
      const filterOptions = [];
      sortAndFilter.map(filter => {
        if (filter.name === 'language') {
          filter.options.map(languageOption => {
            filterOptions.push({
              value: languageOption.optionValue,
              label: languageOption.label,
              trackingId: `LanguageType${languageOption.label}`,
              filterName: filter.label
            });
          });
        }
      });

      return filterOptions;
    } else {
      return null;
    }
  }

  public render() {
    const { showDialog, showRatingFilterSheet, showTravelerFilterSheet, showLanguageFilterSheet } = this.state;
    const { l10n, analytics, context, currentHotel, fullScreen, splitView } = this.props;
    const {
      reviews,
      positiveReviews,
      criticalReviews,
      hotel,
      reviewScoreFilters,
      reviewTravelCompanionFilters,
      reviewTripReasonFilters,
      reviewLanguageFilters,
      sortAndFilter
    } = currentHotel;
    const { ratingCounts } = reviews;
    const questionAnswersViewModel = hotel && hotel.questionAnswersViewModel;
    const toolbarAnalytics = analytics.setupTracker('ReviewsDialog', 'close reviews');
    const toolBarAttributes = {
      type: ToolbarType.CLOSE,
      header: <LocalizedText message="hotels.infosite.reviews.title" />,
      iconLabel: l10n.formatText('hotels.infosite.reviews.cancelButton.a11yLabel')
    };

    const ratingFiltersOptions = [
      {
        value: 5,
        label: `5 - ${l10n.formatText('hotels.infosite.reviews.superlative.excellent')} (${this.getRatingCounts(5, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(5),
        trackingId: 'Rating5'
      },
      {
        value: 4,
        label: `4 - ${l10n.formatText('hotels.infosite.reviews.superlative.good')} (${this.getRatingCounts(4, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(4),
        trackingId: 'Rating4'
      },
      {
        value: 3,
        label: `3 - ${l10n.formatText('hotels.infosite.reviews.superlative.okay')} (${this.getRatingCounts(3, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(3),
        trackingId: 'Rating3'
      },
      {
        value: 2,
        label: `2 - ${l10n.formatText('hotels.infosite.reviews.superlative.mediocre')} (${this.getRatingCounts(2, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(2),
        trackingId: 'Rating2'
      },
      {
        value: 1,
        label: `1 - ${l10n.formatText('hotels.infosite.reviews.superlative.poor')} (${this.getRatingCounts(1, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(1),
        trackingId: 'Rating1'
      }
    ];
    const ratingFilterCount = ratingFiltersOptions.filter(option => option.selected === true).length;

    const travelerTypeFilterOptions = [
      {
        value: TravelerTypeOptions.COUPLES,
        label: l10n.formatText('hotels.infosite.reviews.filterLabel.couples'),
        selected: reviewTravelCompanionFilters && reviewTravelCompanionFilters.includes(ReviewsFilterType.PARTNER),
        trackingId: 'TravelerTypeCouples'
      },
      {
        value: TravelerTypeOptions.SOLO_TRAVELERS,
        label: l10n.formatText('hotels.infosite.reviews.filterLabel.soloTravelers'),
        selected: reviewTravelCompanionFilters && reviewTravelCompanionFilters.includes(ReviewsFilterType.SELF),
        trackingId: 'TravelerTypeSolo'
      },
      {
        value: TravelerTypeOptions.BUSINESS_TRAVELERS,
        label: l10n.formatText('hotels.infosite.reviews.filterLabel.businessTravelers'),
        selected:
          reviewTripReasonFilters &&
          reviewTripReasonFilters.includes(ReviewsFilterType.BUSINESS) &&
          reviewTripReasonFilters.includes(ReviewsFilterType.BUSINESS_LEISURE),
        trackingId: 'TravelerTypeBusiness'
      },
      {
        value: TravelerTypeOptions.FAMILIES,
        label: l10n.formatText('hotels.infosite.reviews.filterLabel.families'),
        selected:
          reviewTravelCompanionFilters &&
          reviewTravelCompanionFilters.includes(ReviewsFilterType.FAMILY) &&
          reviewTravelCompanionFilters.includes(ReviewsFilterType.FAMILY_WITH_CHILDREN),
        trackingId: 'TravelerTypeFamilies'
      },
      {
        value: TravelerTypeOptions.FAMILIES_WITH_CHILDREN,
        label: l10n.formatText('hotels.infosite.reviews.filterLabel.familiesWithChildren'),
        selected:
          reviewTravelCompanionFilters &&
          reviewTravelCompanionFilters.includes(ReviewsFilterType.FAMILY_WITH_CHILDREN) &&
          !reviewTravelCompanionFilters.includes(ReviewsFilterType.FAMILY),
        trackingId: 'TravelerTypeFamiliesWithChildren'
      },
      {
        value: TravelerTypeOptions.GROUPS,
        label: l10n.formatText('hotels.infosite.reviews.filterLabel.groups'),
        selected: reviewTravelCompanionFilters && reviewTravelCompanionFilters.includes(ReviewsFilterType.FRIENDS),
        trackingId: 'TravelerTypeGroups'
      },
      {
        value: TravelerTypeOptions.TRAVELERS_WITH_PETS,
        label: l10n.formatText('hotels.infosite.reviews.filterLabel.travelersWithPets'),
        selected: reviewTravelCompanionFilters && reviewTravelCompanionFilters.includes(ReviewsFilterType.PET),
        trackingId: 'TravelerTypePets'
      }
    ];
    const travelerFilterCount = travelerTypeFilterOptions.filter(option => option.selected === true).length;

    const languageFilterOptions = this.getLanguageFilterOptions(sortAndFilter);
    const languageFilterCount = typeof reviewLanguageFilters === 'string' && reviewLanguageFilters !== '' ? 1 : undefined;

    const dialogContentProps = {
      context,
      reviews,
      l10n,
      questionAnswersViewModel,
      onMoreReviews: this.onMoreReviews,
      positiveReviews,
      criticalReviews,
      applyRatingsFilters: this.applyRatingsFilters,
      applyTravelerTypeFilters: this.applyTravelerTypeFilters,
      applyLanguageTypeFilters: this.applyLanguageTypeFilters,
      propertyId: currentHotel.hotelId,
      ratingFiltersOptions,
      travelerTypeFilterOptions,
      ratingFilterCount,
      travelerFilterCount,
      languageFilterCount,
      showRatingFilterSheet,
      showTravelerFilterSheet,
      showLanguageFilterSheet,
      languageFilterOptions,
      toggleTravelerFilterSheet: this.toggleTravelerFilterSheet,
      toggleRatingFilterSheet: this.toggleRatingFilterSheet,
      toggleLanguageFilterSheet: this.toggleLanguageFilterSheet,
      reviewLanguageFilters
    };

    return (
      <UitkFullscreenDialog dialogShow={showDialog} strictFullscreen={fullScreen}>
        <UitkToolbar {...toolBarAttributes} analytics={toolbarAnalytics} action={this.onDismiss} />
        <UitkDialogContent className="reviews-dialog-main">
          {!splitView && <DialogContent {...dialogContentProps} />}
          {splitView && <DialogContentSplitView {...dialogContentProps} />}
        </UitkDialogContent>
      </UitkFullscreenDialog>
    );
  }
}
